import React, { useState, useCallback, useContext, ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setCookie } from "../../utilities/storageUtilities";
import { LanguagesEntity } from "../../entities/Languages/LanguagesEntity";
import { DropdownMenu } from "../DropdownMenu";
import { SelectChangeEvent } from "@mui/material/Select/Select";
import { OnlineStatusContext } from "contexts/OnlineStatusContext/OnlineStatusContext";
import { IOptions } from "components/DropdownMenu/DropdownMenuInterface";
import { getAvailableLanguages } from "../../utilities/storageUtilities";

interface ILanguagesDropdown {
    className?: string;
    fullWidth?: boolean;
}

export const LanguagesDropdown = ({ className, fullWidth }: ILanguagesDropdown) => {
    const { i18n } = useTranslation();
    const { apolloQueryError, languages } = LanguagesEntity.GetLanguages();
    const browserDefaultLanguage = i18n.language.slice(0, 2);
    const [dropdownValue, setDropdownValue] = useState(browserDefaultLanguage);
    const { onlineStatus } = useContext(OnlineStatusContext);
    const [languagesOptions, setLanguagesOptions] = useState<IOptions[]>([]);

    const tKeys = {
        base: "languages_dropdown",
        title: "title",
        optionsHolder: "languages_options",
        key: "option",
    };

    useEffect(() => {
        if (onlineStatus && languages && !apolloQueryError) {
            setLanguagesOptions(
                languages.map((language) => ({ id: language.code!.toLowerCase(), name: language.name })),
            );
        } else {
            const availableLanguages = getAvailableLanguages();

            if (availableLanguages) {
                setLanguagesOptions(
                    availableLanguages!.map((language) => ({ id: language.code!.toLowerCase(), name: language.name })),
                );
            }
        }
    }, [onlineStatus, languages, apolloQueryError]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<string>, child: ReactNode): void => {
            setDropdownValue(event?.target.value);
            i18n.changeLanguage(event?.target.value.toLowerCase());
            setCookie("user_language", event?.target.value.toLowerCase(), 365);
        },
        [i18n],
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }} className={className}>
            <DropdownMenu
                dropdownValue={dropdownValue}
                handleChange={handleChange}
                tKeys={tKeys}
                options={languagesOptions}
                fullWidth={fullWidth}
            />
        </div>
    );
};
